class InlineFilters {
  constructor() {
    // Redirect from /collections/all to /collections/stories
    if (window.location.pathname === '/collections/all') {
      window.location.href = '/collections/stories';
      return;
    }

    this.container = document.querySelector('[data-inline-filters]');
    this.buttons = this.container?.querySelectorAll('.inline-filters__button');
    this.currentTags = new Set();
    this.isInitialized = false;
    this.isLoading = false;
    this.productGrid = document.querySelector('#product-grid');
    this.productGridContainer = document.querySelector('#ProductGridContainer');
    this.collectionHandle = this.getCollectionHandle();
    this.isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

    // Get translations from the page
    this.translations = this.getTranslations();

    // Check for URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');

    if (filterParam) {
      // Handle URL parameter filtering (loading state already shown by Liquid)
      this.handleUrlParameterFilter(filterParam);
    } else if (this.container) {
      this.initialize();
    }

    // Add global event listener for personalize buttons
    this.addGlobalPersonalizeButtonListeners();

    // If preview mode is enabled via URL, adjust personalize links on initial load
    if (this.isPreviewMode) {
      this.applyPreviewPersonalizeLinks();
    }

    // One-time SEO Hidden filter on initial load (pure frontend; no API)
    try {
      const params = new URLSearchParams(window.location.search);
      const bypassShowAll = params.get('show') === 'true';
      // Toggle search icon visibility based on bypass
      this.toggleSearchIconForSeoHidden(!bypassShowAll);
      if (!bypassShowAll) {
        this.showGridLoadingOverlay('Loading products…');
        // Defer to next tick to allow overlay to paint
        setTimeout(() => {
          try { this.applySeoHiddenOnLoad(); } catch { }
          this.hideGridLoadingOverlay();
        }, 0);
      }
    } catch (e) {
      console.warn('SEO Hidden on-load filter skipped:', e);
    }
  }

  getTranslations() {
    // Get translations from a script tag or data attribute
    const translationScript = document.querySelector('#inline-filters-translations');
    if (translationScript) {
      try {
        return JSON.parse(translationScript.textContent);
      } catch (e) {
        console.warn('Failed to parse translations:', e);
      }
    }

    // Fallback translations
    return {
      empty_state: {
        title: 'Coming Soon',
        image_alt: 'Coming Soon',
        clear_filters: 'Clear all filters?'
      },
      product: {
        sale_badge: 'Sale',
        regular_price: 'Regular price',
        sale_price: 'Sale price',
        unit_price: 'Unit price',
        per: 'per'
      }
    };
  }

  t(key) {
    // Simple translation function
    const keys = key.split('.');
    let value = this.translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Return the key if translation not found
      }
    }

    return value || key;
  }

  initialize() {
    this.initializeCurrentTags();
    this.bindEvents();
    this.isInitialized = true;

    // Add event listeners for personalize buttons on page load
    this.addPersonalizeButtonListeners();

    // Apply preview personalize links if in preview mode
    if (this.isPreviewMode) {
      this.applyPreviewPersonalizeLinks();
    }
  }

  getCollectionHandle() {
    // Extract collection handle from URL
    const pathSegments = window.location.pathname.split('/');
    const collectionIndex = pathSegments.findIndex(segment => segment === 'collections');
    if (collectionIndex !== -1 && pathSegments[collectionIndex + 1]) {
      const handle = pathSegments[collectionIndex + 1];
      // If it's "all", we need to find the actual collection handle
      if (handle === 'all') {
        // Try to get the actual collection handle from the page
        const collectionHandleMeta = document.querySelector('meta[property="og:url"]');
        if (collectionHandleMeta) {
          const url = collectionHandleMeta.getAttribute('content');
          const urlSegments = url.split('/');
          const urlCollectionIndex = urlSegments.findIndex(segment => segment === 'collections');
          if (urlCollectionIndex !== -1 && urlSegments[urlCollectionIndex + 1]) {
            return urlSegments[urlCollectionIndex + 1];
          }
        }

        // Fallback: try to get from Shopify global object
        if (window.Shopify && window.Shopify.theme && window.Shopify.theme.collection) {
          return window.Shopify.theme.collection.handle;
        }

        // If still not found, try to get from the current page context
        const currentCollection = document.querySelector('[data-collection-handle]');
        if (currentCollection) {
          return currentCollection.getAttribute('data-collection-handle');
        }

        // Last resort: try to get from the page URL or use a default
        return 'frontpage'; // Most Shopify stores have a frontpage collection
      }
      return handle;
    }
    return 'frontpage'; // Default fallback
  }

  initializeCurrentTags() {
    // Skip URL parameter processing if already handled in constructor
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');

    if (filterParam) {
      // URL parameters are handled in constructor, skip here
      return;
    }

    // Get tags from URL path (Shopify's native tag filtering)
    const pathSegments = window.location.pathname.split('/');
    const tagSegment = pathSegments[pathSegments.length - 1];

    if (tagSegment && tagSegment !== 'all' && tagSegment !== this.collectionHandle) {
      // Split multiple tags if they exist
      const tags = tagSegment.split('+');
      tags.forEach(tag => {
        if (tag.trim()) {
          this.currentTags.add(tag.trim());
        }
      });
    }

    // Update button states based on current tags
    this.updateButtonStates();
  }

  handleUrlParameterFilter(filterParam) {
    // Clear existing tags
    this.currentTags.clear();

    // Split by + for multiple tags
    const tags = filterParam.split('+');

    // Valid tag categories
    const validTags = [
      'Boy', 'Girl' // Gender
    ];

    tags.forEach(tag => {
      const trimmedTag = tag.trim();
      // Check if it's a valid tag (case-insensitive)
      const isValidTag = validTags.some(validTag =>
        validTag.toLowerCase() === trimmedTag.toLowerCase()
      );

      if (isValidTag && trimmedTag) {
        // Add the properly cased version
        const properCaseTag = validTags.find(validTag =>
          validTag.toLowerCase() === trimmedTag.toLowerCase()
        );
        this.currentTags.add(properCaseTag);
      }
    });

    // Initialize the container if not already done
    if (!this.isInitialized && this.container) {
      this.initialize();
    }

    // Ensure product grid exists
    if (!this.productGrid) {
      this.productGrid = document.querySelector('#product-grid');
    }

    // Update button states
    this.updateButtonStates();

    // Apply filters immediately
    this.fetchFilteredProducts();

    // Don't clean up URL parameter immediately - let it stay until filtering is complete
    // this.cleanupUrlParameter();
  }

  cleanupUrlParameter() {
    // Remove the filter parameter from URL without reloading
    const url = new URL(window.location);
    url.searchParams.delete('filter');

    // Update URL without reloading
    window.history.replaceState({}, '', url.toString());
  }

  updateButtonStates() {
    this.buttons?.forEach(button => {
      const tag = button.dataset.filterTag;
      const isActive = this.currentTags.has(tag);

      if (isActive) {
        button.classList.add('inline-filters__button--active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('inline-filters__button--active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  bindEvents() {
    this.buttons?.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleFilter(button);
      });

      // Add keyboard support
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleFilter(button);
        }
      });
    });
  }

  toggleFilter(button) {
    if (this.isLoading) return; // Prevent multiple requests

    const tag = button.dataset.filterTag;
    const isActive = button.classList.contains('inline-filters__button--active');

    // Check if we're on the gifting collection
    const isGiftingCollection = this.collectionHandle === 'gifting-🎁' ||
      window.location.pathname.includes('/gifting-🎁') ||
      window.location.pathname.includes('/gifting-%F0%9F%8E%81');

    if (isGiftingCollection) {
      // For gifting collection, perform a redirect instead of dynamic filtering
      const baseUrl = '/collections/gifting-🎁';
      if (isActive) {
        // If deactivating, go back to base collection
        window.location.href = baseUrl;
      } else {
        // If activating, redirect to the filtered URL
        window.location.href = `${baseUrl}/${tag}`;
      }
      return;
    }

    // For other collections, use dynamic filtering
    if (isActive) {
      this.currentTags.delete(tag);
      button.classList.remove('inline-filters__button--active');
      button.setAttribute('aria-pressed', 'false');
    } else {
      this.currentTags.add(tag);
      button.classList.add('inline-filters__button--active');
      button.setAttribute('aria-pressed', 'true');
    }

    this.updateURL();
    this.fetchFilteredProducts();

    // After grid updates, re-apply SEO Hidden condition unless ?show=true
    setTimeout(() => {
      try {
        const params = new URLSearchParams(window.location.search);
        const bypass = params.get('show') === 'true';
        this.toggleSearchIconForSeoHidden(!bypass);
        if (!bypass) {
          this.applySeoHiddenOnLoad();
        }
      } catch (_) { }
    }, 0);
  }

  updateURL() {
    const baseUrl = window.location.pathname.split('/').slice(0, -1).join('/');
    let newUrl = baseUrl;

    if (this.currentTags.size > 0) {
      // Use Shopify's native tag filtering format: /collections/all/tag1+tag2+tag3
      const tagsString = Array.from(this.currentTags).join('+');
      newUrl = `${baseUrl}/${tagsString}`;
    }

    // Preserve ?show=true if present
    const hasShowTrue = new URLSearchParams(window.location.search).get('show') === 'true';
    if (hasShowTrue) {
      newUrl += '?show=true';
    }
    // Update URL without reloading the page
    window.history.replaceState({}, '', newUrl);
  }

  // Generate a shareable URL with filter parameters
  generateShareableUrl(tags = null) {
    const tagsToUse = tags || Array.from(this.currentTags);
    const baseUrl = window.location.origin + window.location.pathname;

    if (tagsToUse.length > 0) {
      const tagsString = tagsToUse.join('+');
      return `${baseUrl}?filter=${encodeURIComponent(tagsString)}`;
    }

    return baseUrl;
  }

  // Method to apply filters programmatically (for external use)
  applyFilters(tags) {
    if (!Array.isArray(tags)) {
      console.error('applyFilters expects an array of tags');
      return;
    }

    // Clear existing tags
    this.currentTags.clear();

    // Add new tags
    tags.forEach(tag => {
      if (tag && typeof tag === 'string') {
        this.currentTags.add(tag.trim());
      }
    });

    // Update button states
    this.updateButtonStates();

    // Apply filters
    this.fetchFilteredProducts();
  }

  async fetchFilteredProducts() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.disableButtons();

    try {
      const products = await this.fetchProductsByTags(true);
      this.updateProductGrid(products);

      // Remove the overlay loader when filtering is complete
      this.removeFilterOverlay();

      // Clean up URL parameter after filtering is complete
      this.cleanupUrlParameter();
    } catch (error) {
      console.error('❌ Error fetching filtered products:', error);
      // Show error state
      if (this.productGrid) {
        this.productGrid.innerHTML = `
          <div class="collection collection--empty page-width">
            <div class="title-wrapper center">
              <h2 class="title title--primary">
                Error loading products. Please try again.
                <br>
                <a href="/collections/stories" class="underlined-link link">
                  Return to all products
                </a>
              </h2>
            </div>
          </div>
        `;
      }
      // Remove the overlay loader on error too
      this.removeFilterOverlay();
      // Clean up URL parameter on error too
      this.cleanupUrlParameter();
    } finally {
      this.isLoading = false;
      this.enableButtons();
    }
  }

  removeFilterOverlay() {
    // Remove the existing filter-overlay (from main collection file)
    const overlay = document.getElementById('filter-overlay');
    if (overlay) {
      overlay.style.opacity = '0';
      overlay.style.transition = 'opacity 0.3s ease-out';
      setTimeout(() => {
        overlay.remove();
      }, 300);
    }
  }

  async fetchProductsByTags(fetchAll = false) {
    // Get storefront access token
    const storefrontAccessToken = this.getStorefrontAccessToken();

    if (!storefrontAccessToken) {
      console.error('Storefront Access Token not found. Please check the meta tag in theme.liquid');
      return [];
    }

    // First, let's try to get the correct collection handle
    const collectionHandle = this.collectionHandle;

    // Build GraphQL query
    const query = `
      query getCollectionProducts($handle: String!, $first: Int!, $after: String) {
        collection(handle: $handle) {
          id
          title
          handle
          products(first: $first, after: $after) {
            edges {
              node {
                id
                title
                handle
                description
                  customSeoHidden: metafield(namespace: "custom", key: "seo_hidden") { value }
                featuredImage {
                  url
                  altText
                  width
                  height
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                tags
                variants(first: 1) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
          }
        }
      }
    `;

    const variables = {
      handle: collectionHandle,
      first: fetchAll ? 250 : 50,
      after: null
    };

    const shopDomain = 'bysnaps.myshopify.com';
    const graphqlUrl = `https://${shopDomain}/api/2024-04/graphql.json`;

    const requestBody = JSON.stringify({
      query: query.trim(),
      variables: variables
    });

    try {
      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          'Accept': 'application/json'
        },
        body: requestBody,
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          throw new Error(`GraphQL authentication failed (401). Please check your Storefront Access Token. Response: ${errorText}`);
        } else if (response.status === 403) {
          throw new Error(`GraphQL access denied (403). Please check your Storefront Access Token permissions. Response: ${errorText}`);
        } else {
          throw new Error(`GraphQL request failed: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      const data = await response.json();

      if (data.errors) {
        console.error('❌ GraphQL Errors:', data.errors);
        data.errors.forEach((error, index) => {
          console.error(`Error ${index + 1}:`, error.message);
          if (error.locations) {
            console.error('  Locations:', error.locations);
          }
          if (error.path) {
            console.error('  Path:', error.path);
          }
        });
        return [];
      }

      // If collection is null, try to get available collections
      if (!data.data?.collection) {
        const collections = await this.getAvailableCollections();
        if (collections.length > 0) {
          // Try the first available collection
          const firstCollection = collections[0];
          return this.fetchProductsByCollectionHandle(firstCollection.handle);
        }
        return [];
      }

      let allProducts = [];

      if (data.data?.collection?.products?.edges) {
        allProducts = data.data.collection.products.edges.map(edge => edge.node);
      } else {
        return [];
      }

      // If fetchAll is requested, page through all cursors
      if (fetchAll && data.data?.collection?.products?.pageInfo?.hasNextPage) {
        let after = data.data.collection.products.pageInfo.endCursor;
        while (after) {
          const pageVars = { handle: collectionHandle, first: 250, after };
          const pageBody = JSON.stringify({ query: query.trim(), variables: pageVars });
          const pageResp = await fetch(graphqlUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
              'Accept': 'application/json'
            },
            body: pageBody,
            mode: 'cors',
            credentials: 'omit'
          });
          if (!pageResp.ok) break;
          const pageJson = await pageResp.json();
          const edges = pageJson?.data?.collection?.products?.edges || [];
          allProducts = allProducts.concat(edges.map(e => e.node));
          const pi = pageJson?.data?.collection?.products?.pageInfo;
          after = (pi && pi.hasNextPage) ? pi.endCursor : null;
        }
      }

      // Filter products by selected tags
      if (this.currentTags.size > 0) {
        const originalCount = allProducts.length;
        allProducts = allProducts.filter(product => {
          // Filter by gender only
          const genderTags = ['boy', 'girl'];

          const selectedGenderTags = Array.from(this.currentTags).filter(tag =>
            genderTags.includes(tag.toLowerCase())
          );

          // Check if product matches ANY selected gender (OR logic within gender)
          const hasGender = selectedGenderTags.length === 0 ||
            selectedGenderTags.some(tag =>
              product.tags.some(productTag => productTag.toLowerCase() === tag.toLowerCase())
            );

          return hasGender;
        });
      }

      return allProducts;
    } catch (error) {
      console.error('❌ Error in fetchProductsByTags:', error);
      throw error;
    }
  }

  async getAvailableCollections() {
    const storefrontAccessToken = this.getStorefrontAccessToken();

    const query = `
      query getCollections($first: Int!) {
        collections(first: $first) {
          edges {
            node {
              id
              title
              handle
              productsCount
            }
          }
        }
      }
    `;

    const variables = { first: 10 };

    const shopDomain = 'bysnaps.myshopify.com';
    const graphqlUrl = `https://${shopDomain}/api/2024-04/graphql.json`;

    try {
      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ query: query.trim(), variables }),
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collections: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL Errors:', data.errors);
        return [];
      }

      if (data.data?.collections?.edges) {
        return data.data.collections.edges.map(edge => edge.node);
      }

      return [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      return [];
    }
  }

  async fetchProductsByCollectionHandle(handle) {
    const storefrontAccessToken = this.getStorefrontAccessToken();

    const query = `
      query getCollectionProducts($handle: String!, $first: Int!) {
        collection(handle: $handle) {
          id
          title
          handle
          products(first: $first) {
            edges {
              node {
                id
                title
                handle
                description
                  customSeoHidden: metafield(namespace: "custom", key: "seo_hidden") { value }
                featuredImage {
                  url
                  altText
                  width
                  height
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                tags
                variants(first: 1) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = { handle, first: 50 };

    const shopDomain = 'bysnaps.myshopify.com';
    const graphqlUrl = `https://${shopDomain}/api/2024-04/graphql.json`;

    try {
      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ query: query.trim(), variables }),
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL Errors:', data.errors);
        return [];
      }

      let allProducts = [];

      if (data.data?.collection?.products?.edges) {
        allProducts = data.data.collection.products.edges.map(edge => edge.node);
      }

      // Filter products by selected tags
      if (this.currentTags.size > 0) {
        const originalCount = allProducts.length;
        allProducts = allProducts.filter(product => {
          // Filter by gender only
          const genderTags = ['boy', 'girl'];

          const selectedGenderTags = Array.from(this.currentTags).filter(tag =>
            genderTags.includes(tag.toLowerCase())
          );

          // Check if product matches ANY selected gender (OR logic within gender)
          const hasGender = selectedGenderTags.length === 0 ||
            selectedGenderTags.some(tag =>
              product.tags.some(productTag => productTag.toLowerCase() === tag.toLowerCase())
            );

          return hasGender;
        });
      }

      return allProducts;
    } catch (error) {
      console.error('Error fetching products by handle:', error);
      return [];
    }
  }

  getStorefrontAccessToken() {
    // Check for token in meta tags
    const tokenMeta = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (tokenMeta) {
      return tokenMeta.getAttribute('content');
    }

    // Check for token in window object
    if (window.Shopify && window.Shopify.storefrontAccessToken) {
      return window.Shopify.storefrontAccessToken;
    }

    // Check for token in theme settings
    if (window.theme && window.theme.storefrontAccessToken) {
      return window.theme.storefrontAccessToken;
    }

    return null;
  }

  updateProductGrid(products) {
    if (!this.productGrid) return;

    if (products.length === 0) {
      // Show empty state
      this.productGrid.innerHTML = `
        <div class="collection collection--empty page-width">
          <div class="title-wrapper center">
            <h2 class="title title--primary">
              ${this.t('empty_state.title')}
              <br>
              <img width="100px" src="/cdn/shop/t/6/assets/StarBackground.png" alt="${this.t('empty_state.image_alt')}" class="coming-soon-image">
              <br>
              <a href="/collections/stories" class="underlined-link link">
                ${this.t('empty_state.clear_filters')}
              </a>
            </h2>
          </div>
        </div>
      `;
    } else {
      // Generate product grid HTML that matches the theme's design exactly
      const productsHTML = products.map((product, index) => {
        const variant = product.variants.edges[0]?.node;
        const price = variant?.price?.amount || product.priceRange?.minVariantPrice?.amount || '0';
        const compareAtPrice = variant?.compareAtPrice?.amount;
        const currency = variant?.price?.currencyCode || product.priceRange?.minVariantPrice?.currencyCode || 'USD';
        const imageUrl = product.featuredImage?.url || '';
        const imageAlt = product.featuredImage?.altText || product.title;
        const isAvailable = variant?.availableForSale || false;

        // Format price for display
        const formattedPrice = (parseFloat(compareAtPrice));
        const salePrice = (parseFloat(price));

        // Determine SEO hidden from GraphQL field if present
        const hiddenVal = (product.customSeoHidden?.value ?? '').toString().toLowerCase();
        const isSeoHidden = hiddenVal === '1' || hiddenVal === 'true';

        return `
          <li class="grid__item" ${isSeoHidden ? 'data-seo-hidden="1"' : ''}>
            <div class="card-wrapper product-card-wrapper underline-links-hover" ${isSeoHidden ? 'data-seo-hidden="1"' : ''}>
              <div class="card card--card card--media color-background-1 gradient" style="--ratio-percent: 100.0%;">
                <div class="card__inner ratio" style="--ratio-percent: 100.0%;">
                  <div class="card__media">
                    <div class="media media--transparent media--hover-effect">
                      <img
                        srcset="${imageUrl}&width=165 165w,${imageUrl}&width=360 360w,${imageUrl}&width=533 533w,${imageUrl}&width=720 720w,${imageUrl}&width=940 940w,${imageUrl} 1024w"
                        src="${imageUrl}&width=533"
                        sizes="(min-width: 1600px) 367px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                        alt="${imageAlt}"
                        class="motion-reduce"
                        loading="${index < 2 ? 'eager' : 'lazy'}"
                        width="${product.featuredImage?.width || 1024}"
                        height="${product.featuredImage?.height || 1024}"
                      >
                    </div>
                  </div>
                  <div class="card__content">
                    <div class="card__information">
                      <h3 class="card__heading">
                        <a href="/products/${product.handle}" class="full-unstyled-link">
                          ${product.title}
                        </a>
                      </h3>
                    </div>
                    <div class="card__badge top right">
                      <span class="badge">
                        <span>${this.t('product.sale_badge')}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="card__content">
                  <div class="card__information">
                    <h3 class="card__heading h5">
                      <a href="/products/${product.handle}" class="full-unstyled-link">
                        ${product.title}
                      </a>
                    </h3>
                    <div class="card-information">
                      ${product.description ? `<span class="caption-large light">${product.description.replace(/<[^>]*>/g, '').substring(0, window.innerWidth <= 749 ? 40 : 100)}${product.description.replace(/<[^>]*>/g, '').length > (window.innerWidth <= 749 ? 40 : 100) ? '...' : ''}</span>` : ''}
                      <div class="price price--on-sale">
                        <div class="price__container">
                          <div class="price__regular">
                            <span class="visually-hidden">${this.t('product.regular_price')}</span>
                            <span class="price-item price-item--regular">
                              ${formattedPrice} ${currency}
                            </span>
                          </div>
                          <div class="price__sale">
                            <span class="visually-hidden">${this.t('product.regular_price')}</span>
                            <span>
                              <s class="price-item price-item--regular">
                                ${formattedPrice} ${currency}
                              </s>
                            </span>
                            <span class="visually-hidden">${this.t('product.sale_price')}</span>
                            <span class="price-item price-item--sale price-item--last">
                              ${salePrice} ${currency}
                            </span>
                          </div>
                          <small class="unit-price caption hidden">
                            <span class="visually-hidden">${this.t('product.unit_price')}</span>
                            <span class="price-item price-item--last">
                              <span></span>
                              <span aria-hidden="true">/</span>
                              <span class="visually-hidden">&nbsp;${this.t('product.per')}&nbsp;</span>
                              <span></span>
                            </span>
                          </small>
                        </div>
                      </div>
                      
                      <!-- Personalize Button -->
                      <a href="/products/${product.handle}" class="card__personalize-button">Personalize</a>
                      
                    </div>
                  </div>
                  <div class="card__badge top right">
                    <span class="badge">
                      <span>${this.t('product.sale_badge')}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </li>
        `;
      }).join('');

      this.productGrid.innerHTML = productsHTML;
    }

    // Reinitialize components
    this.reinitializeComponents();
    // Re-apply SEO Hidden after grid render unless ?show=true
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('show') !== 'true') {
        this.showGridLoadingOverlay('Loading products…');
        // Defer to next tick to allow overlay to paint
        setTimeout(() => {
          try {
            // Hide items marked as SEO hidden in dynamic render
            const items = document.querySelectorAll('#product-grid > li, .grid__item');
            items.forEach((li) => {
              const carrier = li.matches('[data-seo-hidden]') ? li : li.querySelector('[data-seo-hidden]');
              const val = carrier?.getAttribute('data-seo-hidden');
              if (val != null) {
                const norm = String(val).toLowerCase();
                if (norm === '1' || norm === 'true') {
                  li.style.display = 'none';
                }
              }
            });
          } catch { }
          this.hideGridLoadingOverlay();
        }, 0);
      }
    } catch (_) { }
  }

  disableButtons() {
    this.buttons?.forEach(button => {
      button.disabled = true;
    });
  }

  enableButtons() {
    this.buttons?.forEach(button => {
      button.disabled = false;
    });
  }

  reinitializeComponents() {
    // Reinitialize any components that might need it

    // Trigger lazy loading for new images
    if (window.lazyLoad) {
      window.lazyLoad.update();
    }

    // Reinitialize scroll triggers if they exist
    if (window.ScrollTrigger) {
      window.ScrollTrigger.refresh();
    }

    // Add event listeners for personalize buttons
    this.addPersonalizeButtonListeners();
    // Re-apply preview personalize links on dynamic updates
    if (this.isPreviewMode) {
      this.applyPreviewPersonalizeLinks();
    }

    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('products-filtered'));
  }

  applyPreviewPersonalizeLinks() {
    try {
      const buttons = document.querySelectorAll('.card__personalize-button');
      buttons.forEach((btn) => {
        let handle = null;
        const href = btn.getAttribute('href') || '';
        const match = href.match(/\/products\/([^\/?#]+)/);
        if (match && match[1]) {
          handle = decodeURIComponent(match[1]);
        } else {
          // Fallback: try to find a sibling link with product URL
          const productLink = btn.closest('.card__content, .card__information, .card').querySelector('a[href^="/products/"]');
          const plHref = productLink?.getAttribute('href') || '';
          const m2 = plHref.match(/\/products\/([^\/?#]+)/);
          if (m2 && m2[1]) {
            handle = decodeURIComponent(m2[1]);
          }
        }
        if (handle) {
          const normalized = handle.replace('-premium', '');
          const newHref = `/pages/preview?story=${encodeURIComponent(normalized)}`;
          btn.setAttribute('href', newHref);
        }
      });
    } catch (err) {
      console.warn('Failed to apply preview personalize links:', err);
    }
  }

  addPersonalizeButtonListeners() {
    // Add event listeners to personalize buttons to prevent card link override
    const personalizeButtons = document.querySelectorAll('.card__personalize-button');
    personalizeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        // Navigate to the button's href
        const href = button.getAttribute('href');
        if (href) {
          window.location.href = href;
        }
      });
    });
  }

  addGlobalPersonalizeButtonListeners() {
    // Add event delegation for all personalize buttons on the page
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('card__personalize-button')) {
        e.stopPropagation();
        e.preventDefault();
        // Navigate to the button's href
        const href = e.target.getAttribute('href');
        if (href) {
          window.location.href = href;
        }
      }
    });
  }

  // Method to get current active tags (for external use)
  getActiveTags() {
    return Array.from(this.currentTags);
  }

  // Method to check if a tag is active
  isTagActive(tag) {
    return this.currentTags.has(tag);
  }
}

// Hide SEO-hidden products once on initial load using data-seo-hidden emitted by Liquid
InlineFilters.prototype.applySeoHiddenOnLoad = function () {
  if (this._seoHiddenApplied) return;
  const items = document.querySelectorAll('#product-grid > li, .grid__item');
  items.forEach((li) => {
    const carrier = li.matches('[data-seo-hidden]') ? li : li.querySelector('[data-seo-hidden]');
    const val = carrier?.getAttribute('data-seo-hidden');
    if (val != null) {
      const norm = String(val).toLowerCase();
      if (norm === '1' || norm === 'true') {
        li.style.display = 'none';
      }
    }
  });
  this._seoHiddenApplied = true;
};

// Grid overlay helpers using the same spinner style as product-uploader
InlineFilters.prototype.showGridLoadingOverlay = function (message) {
  try {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    container.style.filter = 'blur(2px)';
    container.style.position = container.style.position && container.style.position !== 'static' ? container.style.position : 'relative';
    let overlay = container.querySelector('.face-validation-loading');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'face-validation-loading';
      overlay.innerHTML = '<div class="loading-spinner"></div><p></p>';
      container.appendChild(overlay);
    }
    // Ensure styles (reuse product-uploader inline styles)
    try {
      const styleId = 'face-validation-spinner-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } .face-validation-loading { position:absolute!important; inset:0!important; background:rgba(255,255,255,0.95)!important; display:flex!important; flex-direction:column!important; align-items:center!important; justify-content:center!important; z-index: 5!important; border-radius:12px!important; } .face-validation-loading .loading-spinner { width:40px!important; height:40px!important; border:3px solid #f3f4f6!important; border-top:3px solid #8b5cf6!important; border-radius:50%!important; animation: spin 1s linear infinite!important; margin-bottom:1rem!important; display:block!important; } .face-validation-loading p { color:#8b5cf6!important; font-size:0.875rem!important; font-weight:600!important; margin:0!important; text-align:center!important; display:block!important; }';
        document.head.appendChild(style);
      }
    } catch { }
    const text = overlay.querySelector('p');
    if (text) text.textContent = message || 'Loading…';
  } catch { }
};

InlineFilters.prototype.hideGridLoadingOverlay = function () {
  try {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    container.style.filter = '';
    const overlay = container.querySelector('.face-validation-loading');
    if (overlay) overlay.remove();
  } catch { }
};


// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.inlineFilters = new InlineFilters();
  });
} else {
  window.inlineFilters = new InlineFilters();
}

// Export for potential external use
window.InlineFilters = InlineFilters; 